/*
🌙 Premium Theme Toggle Component
Professional theme switching for Safety Intelligence System
*/

import React from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { 
  BulbOutlined, 
  BulbFilled,
  SettingOutlined,
  CheckOutlined,
  SunOutlined,
  MoonOutlined,
  DesktopOutlined
} from '@ant-design/icons';
import { useTheme, getThemeModeLabel } from '../../hooks/useTheme';
import type { ThemeMode } from '../../hooks/useTheme';

interface ThemeToggleProps {
  type?: 'button' | 'icon' | 'dropdown';
  size?: 'small' | 'middle' | 'large';
  showLabel?: boolean;
  placement?: 'topRight' | 'topLeft' | 'bottomRight' | 'bottomLeft';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  type = 'dropdown',
  size = 'middle',
  showLabel = false,
  placement = 'bottomRight'
}) => {
  const { themeMode, isDarkMode, toggleTheme, setThemeMode } = useTheme();

  // Professional theme options
  const themeOptions: MenuProps['items'] = [
    {
      key: 'light',
      icon: <SunOutlined />,
      label: (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          minWidth: 140
        }}>
          <span>Açık Tema</span>
          {themeMode === 'light' && (
            <CheckOutlined style={{ color: '#0052CC' }} />
          )}
        </div>
      ),
      onClick: () => setThemeMode('light'),
    },
    {
      key: 'dark',
      icon: <MoonOutlined />,
      label: (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          minWidth: 140
        }}>
          <span>Koyu Tema</span>
          {themeMode === 'dark' && (
            <CheckOutlined style={{ color: '#0052CC' }} />
          )}
        </div>
      ),
      onClick: () => setThemeMode('dark'),
    },
    {
      type: 'divider',
    },
    {
      key: 'auto',
      icon: <DesktopOutlined />,
      label: (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          minWidth: 140
        }}>
          <span>Otomatik</span>
          {themeMode === 'auto' && (
            <CheckOutlined style={{ color: '#0052CC' }} />
          )}
        </div>
      ),
      onClick: () => setThemeMode('auto'),
    },
  ];

  // Simple toggle button (light/dark only)
  if (type === 'button') {
    return (
      <Tooltip title={isDarkMode ? 'Açık temaya geç' : 'Koyu temaya geç'}>
        <Button
          type="text"
          size={size}
          icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
          onClick={toggleTheme}
          style={{
            color: isDarkMode ? '#fbbf24' : '#0052CC',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {showLabel && (isDarkMode ? 'Açık' : 'Koyu')}
        </Button>
      </Tooltip>
    );
  }

  // Icon only toggle
  if (type === 'icon') {
    return (
      <Tooltip title={`Tema: ${getThemeModeLabel(themeMode)}`}>
        <Button
          type="text"
          size={size}
          icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
          onClick={toggleTheme}
          style={{
            color: isDarkMode ? '#fbbf24' : '#0052CC',
            border: 'none',
            background: 'transparent',
          }}
        />
      </Tooltip>
    );
  }

  // Professional dropdown with all options
  if (type === 'dropdown') {
    return (
      <Dropdown
        menu={{ items: themeOptions }}
        placement={placement}
        trigger={['click']}
        overlayStyle={{ 
          zIndex: 9999,
          minWidth: 180,
        }}
        getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
      >
        <Tooltip title="Tema Ayarları">
          <Button
            type="text"
            size={size}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: isDarkMode ? '#f7f7f7' : '#374151',
              padding: '4px 8px',
            }}
          >
            {themeMode === 'light' && <SunOutlined />}
            {themeMode === 'dark' && <MoonOutlined />}
            {themeMode === 'auto' && <DesktopOutlined />}
            {showLabel && <span style={{ fontSize: 12 }}>{getThemeModeLabel(themeMode)}</span>}
          </Button>
        </Tooltip>
      </Dropdown>
    );
  }

  return null;
};

// Keyboard shortcut hook
export const useThemeToggle = () => {
  const { toggleTheme } = useTheme();

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + T to toggle theme
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'T') {
        event.preventDefault();
        toggleTheme();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleTheme]);

  return toggleTheme;
};
